import React, { useEffect, useState } from 'react';
import { Button, Container, Table, Alert, Spinner, Form, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const Dashboard = ({ onLogout }) => {
  const [contacts, setContacts] = useState([]);
  const [displayedContacts, setDisplayedContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination
  const itemsPerPage = 5;
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch data
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await axios.get('https://admin.rptravelskri.com/get_contacts.php');
        if (response.data.status === 'success') {
          setContacts(response.data.data);
        } else {
          setError('Failed to fetch data.');
        }
      } catch (err) {
        setError('Error fetching contacts.');
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, []);

  // Filter contacts
  useEffect(() => {
    const filtered = contacts.filter(contact =>
      contact.name.toLowerCase().includes(search.toLowerCase()) ||
      contact.email.toLowerCase().includes(search.toLowerCase()) ||
      contact.phone.includes(search)
    );

    const startIdx = (currentPage - 1) * itemsPerPage;
    const paginated = filtered.slice(startIdx, startIdx + itemsPerPage);
    setDisplayedContacts(paginated);
  }, [contacts, search, currentPage]);

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;

    const formData = new FormData();
    formData.append('id', id);

    try {
      const response = await axios.post('https://admin.rptravelskri.com/delete_contact.php', formData);
      if (response.data.status === 'success') {
        setContacts(prev => prev.filter(c => c.id !== id));
      } else {
        alert('Delete failed: ' + response.data.error);
      }
    } catch {
      alert('Server error');
    }
  };

  const totalPages = Math.ceil(
    contacts.filter(contact =>
      contact.name.toLowerCase().includes(search.toLowerCase()) ||
      contact.email.toLowerCase().includes(search.toLowerCase()) ||
      contact.phone.includes(search)
    ).length / itemsPerPage
  );
  const exportToExcel = () => {
    const exportData = contacts.map(({ id, name, phone, email, message }) => ({
      ID: id,
      Name: name,
      Phone: phone,
      Email: email,
      Message: message,
    }));
  
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Contacts");
  
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
  
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `contact_messages_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  
  return (
    <Container className="mt-4">
     <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Admin Dashboard</h3>
        <div>
            <Button variant="success" onClick={exportToExcel} className="me-2">Export to Excel</Button>
            <Button variant="danger" onClick={onLogout}>Logout</Button>
        </div>
    </div>


      <Form className="mb-3">
        <Row>
          <Col md={6}>
            <Form.Control
              type="text"
              placeholder="Search by name, email, or phone"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1); // reset to first page on search
              }}
            />
          </Col>
        </Row>
      </Form>
     

      {loading ? (
        <div className="text-center"><Spinner animation="border" /></div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : contacts.length === 0 ? (
        <Alert variant="info">No messages found.</Alert>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead className="table-dark">
              <tr>
                <th>Sl No.</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Message</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedContacts.map((contact, idx) => (
                <tr key={contact.id}>
                  <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                  <td>{contact.name}</td>
                  <td>{contact.phone}</td>
                  <td>{contact.email}</td>
                  <td>{contact.message}</td>
                  <td>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(contact.id)}>Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Pagination Controls */}
          <div className="d-flex justify-content-center mt-3">
            <Button
              variant="secondary"
              onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="me-2"
            >
              Prev
            </Button>
            <span className="align-self-center">Page {currentPage} of {totalPages}</span>
            <Button
              variant="secondary"
              onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="ms-2"
            >
              Next
            </Button>
          </div>
        </>
      )}
    </Container>
  );
};

export default Dashboard;
